type ApiErrorPayload = {
  code?: unknown;
  message?: unknown;
  meta?: unknown;
};

const apiErrorPayload = (error: unknown): ApiErrorPayload | undefined => {
  if (!error || typeof error !== 'object' || !('response' in error)) return undefined;
  const response = (error as { response?: { data?: unknown } }).response;
  const data = response?.data;
  if (!data || typeof data !== 'object') return undefined;
  if ('error' in data && data.error && typeof data.error === 'object') {
    return data.error as ApiErrorPayload;
  }
  return data as ApiErrorPayload;
};


/** The HTTP status of a failed request, when the server answered. */
export const apiErrorStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object' || !('response' in error)) return undefined;
  const status = (error as { response?: { status?: unknown } }).response?.status;
  return typeof status === 'number' ? status : undefined;
};

export const apiErrorCode = (error: unknown): string | undefined => {
  const code = apiErrorPayload(error)?.code;
  return typeof code === 'string' && code ? code : undefined;
};

/** Structured facts about a coded error, e.g. `{ field: 'textColor' }`. */
export const apiErrorMeta = (error: unknown): Record<string, unknown> | undefined => {
  const meta = apiErrorPayload(error)?.meta;
  return meta && typeof meta === 'object' ? (meta as Record<string, unknown>) : undefined;
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The text for `key`, or null when there is none (i18next returns the key or the empty default). */
const ownText = (t: Translate, key: string, options: Record<string, unknown> = {}): string | null => {
  const text: unknown = t(key, { ...options, defaultValue: '' });
  return typeof text === 'string' && text && text !== key ? text : null;
};

/**
 * The text for an API error the web knows by its code (ClickUp 869ev0jcn):
 * `apiErrors.codes.<CODE>`, or for a refused form the first failed rule,
 * `apiErrors.validation.<rule>`. Null when the code has no text of its own.
 * The server's message is never shown; it is only for developers and logs.
 */
export const knownApiErrorText = (t: Translate, error: unknown): string | null => {
  const code = apiErrorCode(error);
  if (!code) return null;
  const meta = apiErrorMeta(error) ?? {};
  if (code === 'VALIDATION_ERROR') {
    const failures = Array.isArray(meta.fields) ? meta.fields as { rule?: unknown }[] : [];
    if (!failures.length) return null;
    // The first failed rule with a text of its own; any other gets the general one.
    const ruleTexts = failures
      .map(failure => typeof failure?.rule === 'string' ? ownText(t, `apiErrors.validation.${failure.rule}`) : null);
    return ruleTexts.find(Boolean) ?? t('apiErrors.validation.default');
  }
  const field = typeof meta.field === 'string'
    ? ownText(t, `apiErrors.fields.${meta.field}`) ?? meta.field
    : undefined;
  return ownText(t, `apiErrors.codes.${code}`, { ...meta, field });
};

/** The text for an API error: its own text when the code has one, else `fallbackKey`. */
export const apiErrorText = (t: Translate, error: unknown, fallbackKey: string): string =>
  knownApiErrorText(t, error) ?? t(fallbackKey);

/** A request that ran out of time (its own limit or WRITE_TIMEOUT_MS in api.ts). */
export const isRequestTimeout = (error: unknown): boolean => {
  const code = (error as { code?: unknown } | null)?.code;
  return code === 'ECONNABORTED' || code === 'ETIMEDOUT';
};

/**
 * The text for a failed save (ClickUp 869fgxq45). A save that ran out of time
 * says so, since it may still have gone through: an update can simply be
 * tried again, while something new may already exist and should be checked
 * first so it is not created twice.
 */
export const saveErrorText = (
  t: Translate,
  error: unknown,
  fallbackKey: string,
  kind: 'update' | 'create' = 'update'
): string => {
  if (apiErrorCode(error) === 'PING_REFUND_CAPACITY_EXCEEDED') return t('pingRefund.capacityError');
  if (apiErrorCode(error) === 'COMPANY_ORGANIZATION_IDENTITY_LOCKED') return t('common.organizationIdentityLocked');
  // A refusal the API explains by its code (ClickUp 869ev0jcn).
  const known = knownApiErrorText(t, error);
  if (known) return known;
  if (!isRequestTimeout(error)) return t(fallbackKey);
  return t(kind === 'create' ? 'common.createTimedOut' : 'common.saveTimedOut');
};

/**
 * The text for a message that could not be sent: a coded refusal (a file, an
 * attestation reminder) says why, everything else gets `fallbackKey`. A
 * reminder sent too soon says when a new one can be sent (ClickUp 869fmfkyz).
 */
export const messageSendErrorText = (
  t: (key: string, options?: Record<string, unknown>) => string,
  error: unknown,
  fallbackKey: string,
  formatTime: (iso: string) => string
): string => {
  const code = apiErrorCode(error);
  if (code === 'ATTESTATION_REQUEST_TOO_SOON') {
    const nextAt = apiErrorMeta(error)?.nextRequestAllowedAt;
    return typeof nextAt === 'string' && nextAt && !Number.isNaN(new Date(nextAt).getTime())
      ? t('messageAttachments.errors.attestationRequestTooSoonAt', { nextAt: formatTime(nextAt) })
      : t('messageAttachments.errors.codes.ATTESTATION_REQUEST_TOO_SOON');
  }
  const fallback = knownApiErrorText(t, error) ?? t(fallbackKey);
  return code
    ? t(`messageAttachments.errors.codes.${code}`, { defaultValue: fallback })
    : fallback;
};

export const isCurrentPasswordInvalidError = (error: unknown): boolean => {
  const payload = apiErrorPayload(error);
  return (
    payload?.code === 'CURRENT_PASSWORD_INVALID' ||
    payload?.message === 'Current password is invalid.' ||
    payload?.message === 'Current password is incorrect'
  );
};
